import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { tv, VariantProps } from "tailwind-variants";
import { useIsMobile } from "../../../hooks";
import { cn } from "../../../util/classNameConstructor";
import ModalButton from "../../molecules/modalStack/ModalButton";
import { useModals } from "../../molecules/modalStack/ModalProvider";
import Button from "../button/Button";
import Checkbox from "../checkbox/Checkbox";
import Icon from "../icon/Icon";
import type { IconName } from "../icon/names";
import ListItem from "../list/ListItem";
import Menu from "../menu/Menu";
import MenuItem from "../menu/MenuItem";
import Seperator from "../seperator/Seperator";

const SelectMenuStyles = tv({
  slots: {
    wrapper:
      "bg-surface-default border-border-default focusable flex w-full justify-between rounded-xl border",
    description: "text-text-default ml-px text-base",
    trigger:
      "bg-surface-default border-border-default hover:border-border-hovered flex w-full items-center justify-between gap-1 rounded-xl border",
    contentStyle:
      "bg-surface-default border-border-subdued relative mt-1 flex w-full flex-col items-start justify-start rounded-2xl border border-solid shadow-lg",
    itemStyle:
      "focus-visible:outline-text-default group inline-flex h-16 w-full grow items-center gap-4 rounded-xl px-4 py-3 outline-offset-[-2px] focus-visible:outline-2 focus-visible:hover:outline-none",
    help: "text-text-subdued ml-px mt-px flex text-[0.875rem]",
    iconStyle: "shrink-0",
  },
  variants: {
    size: {
      sm: { trigger: "h-7 px-1 text-sm" },
      md: { trigger: "h-9 px-2" },
      lg: { trigger: "h-11 px-2" },
    },
    selectState: {
      focus: { wrapper: "border-focused-black" },
    },
    disabled: {
      true: { wrapper: "text-text-disabled" },
    },
    error: {
      true: {
        trigger: "border-border-critical-default bg-surface-critical-subdued",
        help: "text-text-critical gap-1",
      },
    },
    labelOption: {
      default: { description: " " },
      inline: { description: "" },
      hidden: { description: "invisible" },
    },
    iconOption: {
      default: { iconStyle: "p-px" },
      container: { iconStyle: "bg-palette-yellow-400 rounded-lg p-1.5" },
    },
  },
  defaultVariants: {
    size: "lg",
  },
});

interface ValueProps {
  icon?: string | React.ReactNode;
  iconOption?: "default" | "container";
  label?: string;
  labelOption?: "default" | "inline" | "hidden";
  multiSelect?: boolean;
  data: SelectProps["data"];
  selectedValue: string | string[] | undefined;
  text?: string | string[];
  showSelected?: { text?: boolean; icon?: boolean };
  classNames?: {
    value?: string;
  };
}

const Value: React.FC<ValueProps> = ({
  icon,
  iconOption,
  label,
  labelOption,
  multiSelect,
  data,
  selectedValue,
  text,
  showSelected,
  classNames,
}) => {
  const { iconStyle } = SelectMenuStyles({ iconOption });
  const selectedItem = data?.find(
    ({ value }) =>
      value &&
      selectedValue &&
      (Array.isArray(selectedValue)
        ? selectedValue.includes(value)
        : selectedValue === value),
  );

  const textValue = multiSelect ? (
    <>
      {Array.isArray(selectedValue) && selectedValue.length > 0 ? (
        <>
          {selectedItem?.text ||
            (text && typeof text === "string" && text) ||
            ""}
          {selectedValue.length > 1 ? (
            <span className="body-sm text-text-subdued ml-1">
              +{selectedValue.length - 1}
            </span>
          ) : null}
        </>
      ) : typeof text === "string" ? (
        text
      ) : (
        ""
      )}
    </>
  ) : (showSelected?.text === undefined || showSelected?.text === true) &&
    selectedValue !== undefined &&
    selectedValue !== null &&
    selectedValue !== "" ? (
    selectedItem?.text
  ) : (
    text
  );

  return (
    <div
      className={cn(
        "shrink-1 line-clamp-1 flex items-center gap-1.5",
        classNames?.value,
      )}
    >
      {showSelected?.icon !== false &&
        icon &&
        (typeof icon === "string" ? (
          <Icon
            className={iconStyle()}
            color="currentColor"
            name={icon as IconName}
          />
        ) : (
          <>{icon}</>
        ))}
      {labelOption === "inline" && (
        <label className="text-text-subdued">{label}</label>
      )}
      {textValue && (
        <span className="line-clamp-1 break-all text-left">{textValue}</span>
      )}
    </div>
  );
};

type SelectProps = {
  text?: string | string[];
  data: {
    value?: string;
    text: string | React.ReactNode;
    secondaryContent?: string;
    leadingIcon?: IconName;
    leadingIconProps?: Omit<React.ComponentProps<typeof Icon>, "name">;
    color?: string;
    number?: number;
    category?: string | null;
    selected?: boolean;
    hoverable?: boolean;
    selectable?: boolean;
    iconOption?: "default" | "container";
    className?: string;
  }[];
  content?: React.ReactNode;
  icon?: string | React.ReactNode;
  iconOption?: "default" | "container";
  trailingIcon?: IconName;
  showSelected?: { text?: boolean; icon?: boolean };
  label?: string;
  labelOption?: "default" | "inline" | "hidden";
  helpText?: string;
  selectState?: "focus";
  onValueChange?: (value: string | string[] | null) => void;
  useTriggerWidth?: boolean;
  className?: string;
  classNames?: {
    wrapper?: string;
    trigger?: string;
    description?: string;
    help?: string;
    menu?: string;
    menuItem?: string;
    value?: string;
    trailingIcon?: string;
  };
  onPointerDownOutside?: () => void;
  disabled?: boolean;
  error?: boolean;
  size?: "sm" | "md" | "lg";
  selected?: string | string[];
  multiSelect?: boolean;
  selectable?: boolean;
  removeValues?: boolean;
  tabIndex?: number;
  asModal?: boolean;
};

const Select: React.FC<SelectProps> = ({
  text,
  data,
  content,
  icon,
  iconOption,
  trailingIcon,
  showSelected,
  label,
  labelOption,
  helpText,
  selectState,
  onValueChange,
  useTriggerWidth,
  className,
  classNames,
  onPointerDownOutside,
  disabled,
  error,
  size,
  selected,
  multiSelect = false,
  selectable = false,
  removeValues,
  tabIndex,
  asModal = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { closeModal } = useModals();
  const [open, setOpen] = React.useState(false);
  const { wrapper, trigger, contentStyle, itemStyle, help, description } =
    SelectMenuStyles({
      selectState,
      labelOption,
      disabled: !!disabled,
      error: !!error,
      size,
    });

  const [selectedValue, setSelectedValue] = React.useState<
    string | string[] | undefined
  >(multiSelect ? (selected as string[]) || [] : (selected as string));

  const handleValueChange = useCallback(
    (value: string | null) => {
      if (value === null) {
        setSelectedValue(multiSelect ? [] : undefined);
        onValueChange?.(multiSelect ? [] : null);
        return;
      }

      if (multiSelect) {
        const newSelectedValue = Array.isArray(selectedValue)
          ? selectedValue.includes(value)
            ? selectedValue.filter((v) => v !== value)
            : [...selectedValue, value]
          : [value];

        setSelectedValue(newSelectedValue);
        onValueChange?.(newSelectedValue);
      } else {
        const selectedItem = data?.find((item) => item.value === value);
        if (selectedItem?.value) {
          setSelectedValue(selectedItem.value);
          onValueChange?.(selectedItem.value);
        } else {
          setSelectedValue(undefined);
          onValueChange?.(null);
        }
        setOpen(false);
      }
    },
    [multiSelect, selectedValue, onValueChange, data],
  );

  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const handleOnClick = () => {
    if (!open) {
      setOpen(true);
    }
  };

  const handleOnPointerDownOutside = () => {
    onPointerDownOutside?.();
    setTimeout(() => {
      setOpen(false);
    }, 100);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (removeValues) {
      handleValueChange(null);
    }
  }, [removeValues]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        setOpen(true);
        break;
      case "Escape":
        setOpen(false);
        break;
      case " ":
      case "Enter":
        if (open) {
          const focusedElement = document.activeElement as HTMLElement;
          focusedElement?.click();
        } else {
          setOpen(true);
        }
        break;
      default:
        break;
    }
  };

  if (asModal && isMobile) {
    return (
      <ModalButton
        leadingIcon={<>{icon}</>}
        variant="outline"
        pillShaped={false}
        className={cn("w-full justify-start", className)}
        trailingIcon={trailingIcon || "arrowhead-down-lg"}
        text={text}
        size="lg"
        disabled={disabled}
        options={{
          title: label || t("global:select"),
          swipe: true,
        }}
      >
        <div className="px-3 pb-5">
          {data.map((item, idx) => (
            <div className="flex flex-col w-full">
              <ListItem
                key={item.value || `item-${idx}`}
                leadingIcon={item.leadingIcon}
                primaryContent={item.text}
                classNames={{
                  base: "h-12",
                }}
                onClick={() => {
                  handleValueChange(item.value || null);
                  setTimeout(() => closeModal(), 300);
                }}
              />
              <Seperator
                className="w-[calc(100%-20px)] ml-2.5 my-0 py-0"
                orientation="horizontal"
              />
            </div>
          ))}
        </div>
      </ModalButton>
    );
  }

  return (
    <DropdownMenu.Root open={open}>
      <DropdownMenu.Trigger
        className={cn(wrapper(), className, classNames?.wrapper)}
        onClick={handleOnClick}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        tabIndex={tabIndex}
      >
        {labelOption === "default" && label && (
          <label className={cn(description(), classNames?.description)}>
            {label}
          </label>
        )}
        <div className={cn(trigger(), classNames?.trigger)}>
          <Value
            icon={icon}
            iconOption={iconOption}
            label={label}
            labelOption={labelOption}
            multiSelect={multiSelect}
            data={data}
            selectedValue={selectedValue}
            text={text}
            showSelected={showSelected}
            classNames={classNames}
          />
          {trailingIcon !== false && (
            <Icon
              name={trailingIcon || "arrowhead-down-md"}
              className={cn("shrink-0 mr-0.5", classNames?.trailingIcon)}
            />
          )}
        </div>
      </DropdownMenu.Trigger>
      <Menu
        divider
        useTriggerWidth={useTriggerWidth}
        content={content}
        onPointerDownOutside={handleOnPointerDownOutside}
        multiSelect={multiSelect}
        selectable={selectable}
        handleValueChange={handleValueChange}
        className={classNames?.menu}
        buttons={
          multiSelect && (
            <>
              <Button
                text={t("global:delete")}
                width="max"
                onClick={() => {
                  handleValueChange(null);
                  setOpen(false);
                }}
              />
              <Button
                variant="primary"
                text={t("global:done")}
                width="max"
                onClick={() => setOpen(false)}
              />
            </>
          )
        }
        data={data.map((item, index) => ({
          key: item.value || "",
          id: item.value || "",
          primaryContent: item.text as string,
          secondaryContent: item.secondaryContent,
          leadingIcon: item.leadingIcon,
          leadingIconProps: item.leadingIconProps,
          number: item.number,
          category: item.category || undefined,
          iconOption: item.iconOption || iconOption,
          hoverable: item.hoverable,
          color: item.color,
          value: item.value,
          selectable: selectable,
          selected: multiSelect
            ? Array.isArray(selectedValue) &&
              selectedValue.includes(item.value || "")
            : item.selected || selected === item.value,
          classNames: { base: cn(item.className, classNames?.menuItem) },
          index: index,
          handleValueChange: handleValueChange,
        }))}
      />
      {(error || helpText) && (
        <div className={cn(help(), classNames?.help)}>
          {error && <Icon name="alert" />}
          <label>{helpText}</label>
        </div>
      )}
    </DropdownMenu.Root>
  );
};

export default Select;
